import {makeAutoObservable, type ObservableMap, observable} from 'mobx';

import {Match} from '@/domain/models/Match';
import {MatchMessage} from '@/domain/models/MatchMessage';
import {User} from '@/domain/models/User';
import {isNonNullable} from '@/shared/lib/boolean/isNonNullable';

import {MessageLocationVerificationPlace} from './types';

export type MessageClientStatus = 'sending' | 'failed';

export type Message = MatchMessage & {
  clientId?: string;
  clientStatus?: MessageClientStatus;
  locationVerificationPlace?: MessageLocationVerificationPlace;
  translationUnavailable?: boolean;
};

export type LastViewport = {
  scrollTop: number;
};

export class ByMatchStore {
  readonly matchId: Match['id'];
  fetchedAllOlder = false;
  isRead = true;

  private messagesById = observable.map<Message['id'], Message>();
  private replyTargetMessagesById = observable.map<Message['id'], Message>();
  private lastViewport: LastViewport | undefined;
  private _shouldRefresh: boolean = false;

  constructor(matchId: Match['id']) {
    this.matchId = matchId;

    makeAutoObservable<ByMatchStore, 'lastViewport'>(this, {
      lastViewport: false,
    });
  }

  get shouldRefresh() {
    return this._shouldRefresh;
  }

  setShouldRefresh(shouldRefresh: boolean) {
    this._shouldRefresh = shouldRefresh;
  }

  private mergeMessage(
    currentMessage: Message | undefined,
    nextMessage: Message
  ): Message {
    if (
      currentMessage?.locationVerificationPlace &&
      !nextMessage.locationVerificationPlace
    ) {
      return {
        ...nextMessage,
        locationVerificationPlace: currentMessage.locationVerificationPlace,
      };
    }

    return nextMessage;
  }

  private mergeMessages(
    messageMap: ObservableMap<Message['id'], Message>,
    messages: Message[]
  ) {
    messageMap.merge(
      messages.map((message) => {
        const currentMessage = messageMap.get(message.id);

        return [
          message.id,
          this.mergeMessage(currentMessage, message),
        ] as const;
      })
    );
  }

  get orderIds() {
    return [...this.messagesById.values()]
      .sort((a, b) => b.created_at - a.created_at)
      .map((message) => message.id);
  }

  get messages() {
    return this.orderIds
      .map((id) => this.messagesById.get(id))
      .filter(isNonNullable);
  }

  get replyTargetMessages() {
    return [...this.replyTargetMessagesById.values()];
  }

  get oldestCreatedAt(): number | undefined {
    return this.getMessage(this.orderIds[this.orderIds.length - 1])?.created_at;
  }

  getCountUnreadFromUser(userId: User['id'], lastMeReadAt?: number) {
    let unread = 0;
    if (!lastMeReadAt) {
      return unread;
    }

    for (let i = 0; i < this.messages.length; i++) {
      const message = this.messages[i];
      if (message.created_at < lastMeReadAt) {
        break;
      }

      if (message.sender_user_id === userId) {
        unread += 1;
      }
    }

    return unread;
  }

  addMessages(messages: Message[]) {
    this.mergeMessages(this.messagesById, messages);
  }

  private replaceMessages(messages: Message[]) {
    // Local-only messages (sending/failed) are not known to the server, keep
    // them so they are not lost on a refresh.
    const localMessages = [...this.messagesById.values()].filter(
      (message) => message.clientStatus
    );

    this.messagesById.replace(
      [...localMessages, ...messages].map((message) => [message.id, message])
    );
  }

  applyLatestMessages(messages: Message[]): boolean {
    const topMessage = this.messages.find((message) => !message.clientStatus);

    if (topMessage) {
      const topMessageIndex = messages.findIndex(
        (message) => message.id === topMessage.id
      );

      if (topMessageIndex >= 0) {
        this.addMessages(messages.slice(0, topMessageIndex));
        return true;
      }
    }

    this.replaceMessages(messages);
    this.fetchedAllOlder = false;

    return false;
  }

  addReplyTargetMessages(messages: Message[]) {
    this.mergeMessages(this.replyTargetMessagesById, messages);
  }

  syncMessage(message: Message): boolean {
    if (!this.messagesById.has(message.id)) {
      return false;
    }

    this.mergeMessages(this.messagesById, [message]);
    return true;
  }

  getReplyTargetMessage(messageId: Message['id']) {
    return this.replyTargetMessagesById.get(messageId);
  }

  syncReplyTargetMessage(message: Message): boolean {
    if (!this.replyTargetMessagesById.has(message.id)) {
      return false;
    }

    this.mergeMessages(this.replyTargetMessagesById, [message]);
    return true;
  }

  detachMessagesReplyingTo(targetMessageId: Message['id']) {
    for (const message of this.messagesById.values()) {
      if (message.reply_to_message_id === targetMessageId) {
        message.reply_to_message_id = undefined;
      }
    }

    for (const message of this.replyTargetMessagesById.values()) {
      if (message.reply_to_message_id === targetMessageId) {
        message.reply_to_message_id = undefined;
      }
    }
  }

  setFetchedAllOlder(value: boolean) {
    this.fetchedAllOlder = value;
  }

  updateMessage(messageId: Message['id'], updater: (message: Message) => void) {
    const message = this.messagesById.get(messageId);
    if (!message) {
      return;
    }

    updater(message);
  }

  replaceMessage(oldMessageId: Message['id'], nextMessage: Message) {
    this.messagesById.delete(oldMessageId);
    this.messagesById.set(nextMessage.id, nextMessage);
  }

  getMessage(messageId: Message['id']) {
    return this.messagesById.get(messageId);
  }

  removeMessage(messageId: Message['id']) {
    this.messagesById.delete(messageId);
    this.replyTargetMessagesById.delete(messageId);
  }

  getLastMessage() {
    const lastMessageId = this.orderIds[0];

    if (lastMessageId === undefined) {
      return undefined;
    }

    return this.messagesById.get(lastMessageId);
  }

  setLastViewport(value: LastViewport) {
    this.lastViewport = value;
  }

  getLastViewport() {
    return this.lastViewport;
  }

  clearLastViewport() {
    this.lastViewport = undefined;
  }

  hasMessages() {
    return this.messagesById.size > 0;
  }
}
